/**
 * AttachDraftPicker — "open a document in this chat" modal, two sources:
 *
 *   - My Documents      — the user's saved DraftDocuments (server search).
 *     Picking one fetches the FULL draft (the list endpoint strips docHtml).
 *   - Platform templates — the stock catalog (60 resolutions + document
 *     library), rendered client-side via getDocumentPreview with the active
 *     company's details. No network round-trip at all.
 *
 * Perf notes: no backdrop blur (it made scrolling the list janky over the
 * busy chat page), skeleton rows instead of a spinner, and the list is
 * layout-contained so hover/scroll stays cheap.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { FiFileText, FiLayers, FiSearch, FiX } from 'react-icons/fi';
import SegmentedControl from '../common/SegmentedControl';
import { draftsAPI, recipesAPI } from '../../services/api';
import { DOCUMENTS } from '../../data/documents';
import { RESOLUTIONS } from '../../data/resolutions';
import { REPORT_TEMPLATES } from '../../data/reportTemplates';

const TYPE_LABEL = { resolution: 'Resolution', minutes: 'Minutes', document: 'Document', report: 'Report' };

// A recipe's docKind is finer-grained than basedOn — label the row with it when
// it's specific (not 'other'), else fall back to the basedOn TYPE_LABEL.
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const recipeKindLabel = (r) =>
  r.docKind && r.docKind !== 'other' ? capitalize(r.docKind) : (TYPE_LABEL[r.basedOn] || 'Document');

// The full stock catalog, grouped for display. Minutes have no static template
// (they're built in the Minutes page from a meeting config) — saved minutes
// open from the My Documents tab like any other draft.
const TEMPLATE_GROUPS = [
  { label: 'Resolutions', items: RESOLUTIONS.map((r) => ({ id: r.id, title: r.title, sub: r.subtitle, kind: 'resolution' })) },
  { label: 'Document Library', items: DOCUMENTS.map((d) => ({ id: d.id, title: d.title, sub: d.section, kind: 'document' })) },
  { label: 'Reports', items: REPORT_TEMPLATES.map((r) => ({ id: r.id, title: r.title, sub: r.section, kind: 'report' })) },
];
const TEMPLATE_COUNT = TEMPLATE_GROUPS.reduce((n, g) => n + g.items.length, 0);

const timeAgo = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const d = Math.floor((Date.now() - t) / 86400000);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Shimmering placeholder rows shown while the saved-documents list loads.
function SkeletonRows() {
  return (
    <div className="flex flex-col gap-1.5" aria-hidden="true">
      {[92, 70, 84, 63, 78, 88].map((w, i) => (
        <div key={i} className="flex items-center gap-2.5 px-2.5 py-2">
          <div className="w-8 h-8 rounded-lg shrink-0 cs-skeleton" />
          <div className="flex-1 min-w-0">
            <div className="h-3 rounded cs-skeleton" style={{ width: `${w}%` }} />
            <div className="h-2.5 rounded cs-skeleton mt-1.5" style={{ width: `${Math.max(30, w - 35)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Row({ icon, title, sub, loading, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[10px] text-left hover:bg-[#faf7f2] transition-colors disabled:opacity-60"
    >
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-[rgba(20,95,95,0.08)] text-[#145f5f]">
        {loading
          ? <span className="w-3.5 h-3.5 rounded-full border-2 border-[#ddd6c6] border-t-[#145f5f] animate-spin" />
          : icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-semibold text-[#08111f] truncate">{title}</span>
        <span className="block text-[10.5px] text-[#7a7060] truncate">{sub}</span>
      </span>
    </button>
  );
}

export default function AttachDraftPicker({ open, onClose, onPick, onPickTemplate, onPickRecipe }) {
  const [tab, setTab] = useState('mine');
  const [query, setQuery] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickingId, setPickingId] = useState(null);
  const inputRef = useRef(null);

  // Load (and reload on debounced search) while the My Documents tab is open.
  useEffect(() => {
    if (!open || tab !== 'mine') return undefined;
    setLoading(true);
    const t = setTimeout(() => {
      draftsAPI.getAll({ search: query || undefined, limit: 25 })
        .then((res) => setDrafts(res?.data?.drafts || res?.data || []))
        .catch(() => setDrafts([]))
        .finally(() => setLoading(false));
    }, query ? 300 : 0);
    return () => clearTimeout(t);
  }, [open, tab, query]);

  // My Templates tab — the user's firm recipes (server search, same debounce).
  useEffect(() => {
    if (!open || tab !== 'recipes') return undefined;
    setLoading(true);
    const t = setTimeout(() => {
      recipesAPI.getAll({ search: query || undefined, status: 'active', limit: 25 })
        .then((res) => setRecipes(res?.data || []))
        .catch(() => setRecipes([]))
        .finally(() => setLoading(false));
    }, query ? 300 : 0);
    return () => clearTimeout(t);
  }, [open, tab, query]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setTab('mine');
    setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep focus in the search box when switching tabs.
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0); }, [tab, open]);

  const templateGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TEMPLATE_GROUPS
      .map((g) => ({
        ...g,
        items: q ? g.items.filter((t) => `${t.title} ${t.sub || ''}`.toLowerCase().includes(q)) : g.items,
      }))
      .filter((g) => g.items.length > 0);
  }, [query]);

  if (!open) return null;

  const pickMine = async (row) => {
    setPickingId(row._id);
    try {
      const res = await draftsAPI.getById(row._id);
      if (res?.success && res.data) onPick(res.data);
      else onPick(null, 'Could not load that document.');
    } catch (err) {
      onPick(null, err?.message || 'Could not load that document.');
    } finally {
      setPickingId(null);
    }
  };

  const list = Array.isArray(drafts) ? drafts : [];

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4"
      style={{ background: 'rgba(8,17,31,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-[540px] max-w-[94vw] overflow-hidden flex flex-col" style={{ boxShadow: '0 12px 40px rgba(8,17,31,0.16)', height: 'min(600px, 78vh)' }}>
        <div className="px-4 pt-4 pb-3 border-b" style={{ borderColor: '#eee6d8' }}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[17px] font-bold text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Open a document in this chat
            </div>
            <button type="button" onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-[#9a9080] hover:text-[#08111f] hover:bg-[#f0ece2] transition-colors" aria-label="Close">
              <FiX size={15} />
            </button>
          </div>
          <div className="text-[11.5px] text-[#7a7060] mb-2.5">It opens beside the chat — ask Vee about it or tell it what to change.</div>
          <SegmentedControl
            value={tab}
            onChange={setTab}
            options={[
              { key: 'mine', label: 'My Documents' },
              { key: 'templates', label: 'Platform', count: TEMPLATE_COUNT },
              { key: 'recipes', label: 'My Templates' },
            ]}
            className="mb-2.5"
          />
          <div className="relative">
            <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9a9080]" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                tab === 'mine'
                  ? 'Search your documents…'
                  : tab === 'recipes'
                    ? 'Search your templates…'
                    : 'Search resolutions, notices, letters, agreements…'
              }
              className="w-full h-9 pl-9 pr-3 rounded-lg border bg-white text-[12.5px] outline-none transition-colors focus:border-[#145f5f]"
              style={{ borderColor: '#ddd6c6' }}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2.5" style={{ contain: 'content' }}>
          {tab === 'mine' ? (
            loading ? (
              <SkeletonRows />
            ) : list.length === 0 ? (
              <div className="text-center py-10">
                <div className="text-[13px] font-semibold text-[#08111f]">No documents found</div>
                <div className="text-[11.5px] text-[#7a7060] mt-1">{query ? `Nothing matches “${query}”.` : 'Documents you save appear here — or open a platform template instead.'}</div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {list.map((row) => (
                  <Row
                    key={row._id}
                    icon={<FiFileText size={14} />}
                    title={row.documentTitle || 'Untitled document'}
                    sub={[TYPE_LABEL[row.documentType] || 'Document', row.refNumber, row.companyName, row.updatedAt ? timeAgo(row.updatedAt) : null].filter(Boolean).join(' · ')}
                    loading={pickingId === row._id}
                    onClick={() => pickMine(row)}
                  />
                ))}
              </div>
            )
          ) : tab === 'recipes' ? (
            loading ? (
              <SkeletonRows />
            ) : recipes.length === 0 ? (
              <div className="text-center py-10">
                <div className="text-[13px] font-semibold text-[#08111f]">{query ? 'No templates match' : 'No templates yet'}</div>
                <div className="text-[11.5px] text-[#7a7060] mt-1">
                  {query
                    ? `Nothing matches “${query}”.`
                    : 'Ask Vee to “save this as my standard format” after a draft you like — approved templates appear here.'}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {recipes.map((r) => (
                  <Row
                    key={r._id}
                    icon={<FiLayers size={14} />}
                    title={r.name}
                    sub={[
                      recipeKindLabel(r),
                      `${(r.sections || []).length} section${(r.sections || []).length === 1 ? '' : 's'}`,
                      r.version > 1 ? `v${r.version}` : null,
                    ].filter(Boolean).join(' · ')}
                    onClick={() => onPickRecipe && onPickRecipe(r)}
                  />
                ))}
              </div>
            )
          ) : templateGroups.length === 0 ? (
            <div className="text-center py-10">
              <div className="text-[13px] font-semibold text-[#08111f]">No templates match</div>
              <div className="text-[11.5px] text-[#7a7060] mt-1">Try “director”, “AGM”, “share”, “NDA”, “board report”…</div>
            </div>
          ) : (
            templateGroups.map((g) => (
              <div key={g.label} className="mb-2 last:mb-0">
                <div className="px-2.5 pt-1.5 pb-1 text-[9.5px] font-bold uppercase tracking-[0.8px] text-[#9a9080]">
                  {g.label} · {g.items.length}
                </div>
                <div className="flex flex-col gap-1">
                  {g.items.map((t) => (
                    <Row
                      key={`${t.kind}-${t.id}`}
                      icon={<FiFileText size={14} />}
                      title={t.title}
                      sub={[TYPE_LABEL[t.kind], t.sub].filter(Boolean).join(' · ')}
                      onClick={() => onPickTemplate(t)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Local shimmer for the skeleton rows */}
      <style>{`
        .cs-skeleton {
          background: linear-gradient(90deg, #f0ece2 25%, #f8f5ee 45%, #f0ece2 65%);
          background-size: 200% 100%;
          animation: cs-skeleton-sweep 1.2s ease-in-out infinite;
        }
        @keyframes cs-skeleton-sweep { to { background-position: -200% 0; } }
      `}</style>
    </div>
  );
}
